import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    LogOut,
    MailOpen,
    Mail,
    Trash2,
    Inbox,
    Handshake,
    UserPlus,
    RefreshCw,
} from "lucide-react";
import { SEO } from "../components/SEO";
import CommunityAdmin from "../components/CommunityAdmin";
import HotelsAdmin from "../components/HotelsAdmin";
import { API, LOGO_MARK } from "../lib/site";

const TOKEN_KEY = "ggh_admin_token";

const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

const formatDate = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
};

const LoginForm = ({ onLogin }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, { email, password });
            localStorage.setItem(TOKEN_KEY, data.token);
            onLogin(data.token);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="grain flex min-h-screen items-center justify-center bg-[#04150D] px-5">
            <form
                onSubmit={submit}
                data-testid="admin-login-form"
                className="w-full max-w-md rounded-2xl border border-white/10 bg-[#07211A] p-9"
            >
                <img src={LOGO_MARK} alt="Global Green Hotel logo" className="w-36" />
                <h1 className="font-display mt-7 text-3xl font-medium text-[#FDFBF7]">Admin Sign In</h1>
                <p className="mt-2 text-sm text-[#FDFBF7]/60">
                    Private area for Global Green Hotel administration.
                </p>
                <label htmlFor="admin-email" className="mt-8 mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                    Email
                </label>
                <input
                    id="admin-email"
                    data-testid="admin-login-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field"
                    placeholder="you@example.com"
                    autoComplete="username"
                />
                <label htmlFor="admin-password" className="mt-5 mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-[#FDFBF7]/70">
                    Password
                </label>
                <input
                    id="admin-password"
                    data-testid="admin-login-password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field"
                    placeholder="••••••••"
                    autoComplete="current-password"
                />
                {error && (
                    <p data-testid="admin-login-error" role="alert" className="mt-4 text-sm font-semibold text-[#E8A0A0]">
                        {error}
                    </p>
                )}
                <button
                    type="submit"
                    data-testid="admin-login-submit"
                    disabled={busy}
                    className="btn-gold mt-7 w-full disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {busy ? "Signing in…" : "Sign In"}
                </button>
            </form>
        </div>
    );
};

const SubmissionCard = ({ item, onToggleRead, onDelete }) => {
    const chip = item.reason || item.interest_area || "Individual Registration";
    return (
        <article
            data-testid={`submission-${item.id}`}
            className={`rounded-2xl border p-6 transition-colors duration-200 ${
                item.read ? "border-white/8 bg-[#07211A]/60" : "border-[#C2A160]/40 bg-[#07211A]"
            }`}
        >
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-display text-xl font-medium text-[#FDFBF7]">{item.name}</h3>
                        {!item.read && (
                            <span className="rounded-full bg-[#C2A160] px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#0A1711]">
                                New
                            </span>
                        )}
                        <span className="rounded-full border border-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#FDFBF7]/60">
                            {chip}
                        </span>
                    </div>
                    <p className="mt-1 text-sm text-[#D9B96A]">{item.email}</p>
                    <p className="mt-1 text-xs text-[#FDFBF7]/50">
                        {[item.organization || item.profession, item.country, formatDate(item.created_at)]
                            .filter(Boolean)
                            .join(" · ")}
                    </p>
                </div>
                <div className="flex gap-2">
                    <button
                        type="button"
                        data-testid={`submission-read-${item.id}`}
                        onClick={() => onToggleRead(item)}
                        aria-label={item.read ? "Mark as unread" : "Mark as read"}
                        title={item.read ? "Mark as unread" : "Mark as read"}
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#FDFBF7]/70 transition-colors hover:border-[#C2A160] hover:text-[#C2A160]"
                    >
                        {item.read ? <Mail size={16} /> : <MailOpen size={16} />}
                    </button>
                    <button
                        type="button"
                        data-testid={`submission-delete-${item.id}`}
                        onClick={() => onDelete(item)}
                        aria-label="Delete submission"
                        title="Delete submission"
                        className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-[#FDFBF7]/70 transition-colors hover:border-red-400 hover:text-red-400"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>
            {item.contributions && item.contributions.length > 0 && (
                <p className="mt-4 border-t border-white/8 pt-4 text-xs font-bold uppercase tracking-wider text-[#C2A160]">
                    Contributes: <span className="font-semibold normal-case tracking-normal text-[#FDFBF7]/75">{item.contributions.join(", ")}</span>
                </p>
            )}
            {item.message && (
                <p className="mt-4 whitespace-pre-line border-t border-white/8 pt-4 text-sm leading-relaxed text-[#FDFBF7]/75">
                    {item.message}
                </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-4">
                {item.phone && <p className="text-xs text-[#FDFBF7]/50">Phone: {item.phone}</p>}
                {item.document_url && (
                    <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${item.document_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`submission-document-${item.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#D9B96A] transition-colors hover:text-[#C2A160]"
                    >
                        View document{item.original_filename ? `: ${item.original_filename}` : ""}
                    </a>
                )}
            </div>
        </article>
    );
};

const Dashboard = ({ token, onLogout }) => {
    const [data, setData] = useState({ contacts: [], interests: [], registrations: [] });
    const [tab, setTab] = useState("contacts");
    const [loading, setLoading] = useState(true);

    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/submissions`, authHeaders);
            setData(data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem(TOKEN_KEY);
                onLogout();
            } else {
                toast.error("Could not load submissions.");
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        load();
    }, [load]);

    const toggleRead = async (item) => {
        try {
            await axios.patch(`${API}/admin/submissions/${item.id}`, { read: !item.read }, authHeaders);
            setData((d) => ({
                contacts: d.contacts.map((c) => (c.id === item.id ? { ...c, read: !item.read } : c)),
                interests: d.interests.map((c) => (c.id === item.id ? { ...c, read: !item.read } : c)),
                registrations: (d.registrations || []).map((c) => (c.id === item.id ? { ...c, read: !item.read } : c)),
            }));
        } catch {
            toast.error("Could not update the submission.");
        }
    };

    const remove = async (item) => {
        if (!window.confirm("Delete this submission permanently?")) return;
        try {
            await axios.delete(`${API}/admin/submissions/${item.id}`, authHeaders);
            setData((d) => ({
                contacts: d.contacts.filter((c) => c.id !== item.id),
                interests: d.interests.filter((c) => c.id !== item.id),
                registrations: (d.registrations || []).filter((c) => c.id !== item.id),
            }));
            toast.success("Submission deleted.");
        } catch {
            toast.error("Could not delete the submission.");
        }
    };

    const items =
        tab === "contacts" ? data.contacts : tab === "interests" ? data.interests : data.registrations || [];
    const unreadContacts = data.contacts.filter((c) => !c.read).length;
    const unreadInterests = data.interests.filter((c) => !c.read).length;
    const unreadRegistrations = (data.registrations || []).filter((c) => !c.read).length;

    return (
        <div className="grain min-h-screen bg-[#04150D]">
            <div className="relative z-[2] mx-auto max-w-[1100px] px-5 py-28 sm:px-8">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <p className="eyebrow-gold">GGH Admin</p>
                        <h1 className="font-display mt-2 text-3xl font-medium text-[#FDFBF7] sm:text-4xl">
                            Submissions
                        </h1>
                    </div>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            data-testid="admin-refresh"
                            onClick={load}
                            className="btn-outline-light !px-5 !py-2.5"
                        >
                            <RefreshCw size={15} aria-hidden="true" />
                            Refresh
                        </button>
                        <button
                            type="button"
                            data-testid="admin-logout"
                            onClick={onLogout}
                            className="btn-outline-light !px-5 !py-2.5"
                        >
                            <LogOut size={15} aria-hidden="true" />
                            Sign Out
                        </button>
                    </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-3" role="tablist" aria-label="Submission types">
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === "contacts"}
                        data-testid="admin-tab-contacts"
                        onClick={() => setTab("contacts")}
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${
                            tab === "contacts"
                                ? "bg-[#C2A160] text-[#0A1711]"
                                : "border border-white/15 text-[#FDFBF7]/70 hover:border-[#C2A160] hover:text-[#C2A160]"
                        }`}
                    >
                        <Inbox size={15} aria-hidden="true" />
                        Contact Messages ({data.contacts.length})
                        {unreadContacts > 0 && (
                            <span className="rounded-full bg-[#04150D] px-2 py-0.5 text-[10px] font-extrabold text-[#D9B96A]">
                                {unreadContacts} new
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === "interests"}
                        data-testid="admin-tab-interests"
                        onClick={() => setTab("interests")}
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${
                            tab === "interests"
                                ? "bg-[#C2A160] text-[#0A1711]"
                                : "border border-white/15 text-[#FDFBF7]/70 hover:border-[#C2A160] hover:text-[#C2A160]"
                        }`}
                    >
                        <Handshake size={15} aria-hidden="true" />
                        Interest Submissions ({data.interests.length})
                        {unreadInterests > 0 && (
                            <span className="rounded-full bg-[#04150D] px-2 py-0.5 text-[10px] font-extrabold text-[#D9B96A]">
                                {unreadInterests} new
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={tab === "registrations"}
                        data-testid="admin-tab-registrations"
                        onClick={() => setTab("registrations")}
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200 ${
                            tab === "registrations"
                                ? "bg-[#C2A160] text-[#0A1711]"
                                : "border border-white/15 text-[#FDFBF7]/70 hover:border-[#C2A160] hover:text-[#C2A160]"
                        }`}
                    >
                        <UserPlus size={15} aria-hidden="true" />
                        Registrations ({(data.registrations || []).length})
                        {unreadRegistrations > 0 && (
                            <span className="rounded-full bg-[#04150D] px-2 py-0.5 text-[10px] font-extrabold text-[#D9B96A]">
                                {unreadRegistrations} new
                            </span>
                        )}
                    </button>
                </div>

                <div className="mt-8 grid gap-4" data-testid="admin-submissions-list">
                    {loading ? (
                        <p className="py-16 text-center text-sm text-[#FDFBF7]/50">Loading submissions…</p>
                    ) : items.length === 0 ? (
                        <p data-testid="admin-empty" className="rounded-2xl border border-white/10 bg-[#07211A]/60 py-16 text-center text-sm text-[#FDFBF7]/50">
                            No submissions yet. New messages will appear here.
                        </p>
                    ) : (
                        items.map((item) => (
                            <SubmissionCard
                                key={item.id}
                                item={item}
                                onToggleRead={toggleRead}
                                onDelete={remove}
                            />
                        ))
                    )}
                </div>

                <CommunityAdmin token={token} />

                <HotelsAdmin token={token} />
            </div>
        </div>
    );
};

const Admin = () => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
    };

    return (
        <main>
            <SEO title="Admin | Global Green Hotel" description="Private administration area for Global Green Hotel." />
            {token ? (
                <Dashboard token={token} onLogout={logout} />
            ) : (
                <LoginForm onLogin={setToken} />
            )}
        </main>
    );
};

export default Admin;
